import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Solicitacao } from '@/types/solicitacao';
import { useClientFilter } from '@/hooks/useClientFilter';

export const useDashboardData = () => {
  const { getClientFilter, clientLoading } = useClientFilter();
  const clientId = getClientFilter();

  const { data: tickets = [], isLoading: ticketsLoading } = useQuery({
    queryKey: ['dashboard-tickets', clientId],
    queryFn: async () => {
      let query = supabase
        .from('solicitacoes')
        .select(`
          *,
          categoria:categorias_servico(nome),
          solicitante:users!solicitacoes_solicitante_id_fkey(name),
          cliente:clients(name),
          grupo_responsavel:groups(name),
          atendente:users!solicitacoes_atendente_id_fkey(name)
        `)
        .order('criado_em', { ascending: false })
        .limit(10);

      if (clientId) {
        query = query.eq('client_id', clientId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as Solicitacao[];
    },
    enabled: !clientLoading,
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats', clientId],
    queryFn: async () => {
      // Buscar contagens de tickets por status
      let statusQuery = supabase
        .from('solicitacoes')
        .select('status')
        .not('status', 'eq', 'fechada');

      if (clientId) {
        statusQuery = statusQuery.eq('client_id', clientId);
      }

      const { data: statusCount, error: statusError } = await statusQuery;
      if (statusError) throw statusError;

      // Buscar tickets resolvidos hoje
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let resolvedQuery = supabase
        .from('solicitacoes')
        .select('id')
        .eq('status', 'resolvida')
        .gte('data_resolucao', today.toISOString());

      if (clientId) {
        resolvedQuery = resolvedQuery.eq('client_id', clientId);
      }

      const { data: resolvedToday, error: resolvedError } = await resolvedQuery;
      if (resolvedError) throw resolvedError;

      // Buscar tickets em risco de SLA
      const now = new Date();
      let slaQuery = supabase
        .from('solicitacoes')
        .select('id, data_limite_resolucao')
        .not('status', 'in', '("resolvida","fechada")')
        .not('data_limite_resolucao', 'is', null)
        .lt('data_limite_resolucao', now.toISOString());

      if (clientId) {
        slaQuery = slaQuery.eq('client_id', clientId);
      }

      const { data: slaRisk, error: slaError } = await slaQuery;
      if (slaError) throw slaError;

      // Buscar tickets do meu grupo (onde o usuário pertence ao grupo responsável)
      const currentUser = await supabase.auth.getUser();
      const userId = currentUser.data.user?.id;
      
      if (!userId) {
        console.log('Usuário não autenticado, pulando busca de grupos');
        return { myGroupTickets: 0 };
      }

      const { data: userGroups, error: userGroupsError } = await supabase
        .from('user_groups')
        .select('group_id')
        .eq('user_id', userId);

      if (userGroupsError) {
        console.error('Erro ao buscar grupos do usuário:', userGroupsError);
      }

      const groupIds = userGroups?.map(ug => ug.group_id) || [];
      
      let myGroupTickets = [];
      if (groupIds.length > 0) {
        let groupTicketsQuery = supabase
          .from('solicitacoes')
          .select('id')
          .in('grupo_responsavel_id', groupIds)
          .not('status', 'in', '("resolvida","fechada")');

        if (clientId) {
          groupTicketsQuery = groupTicketsQuery.eq('client_id', clientId);
        }

        const { data: groupTickets, error: groupTicketsError } = await groupTicketsQuery;

        if (groupTicketsError) {
          console.error('Erro ao buscar tickets do meu grupo:', groupTicketsError);
        } else {
          myGroupTickets = groupTickets || [];
        }
      }

      const totalOpen = statusCount?.length || 0;
      const slaAtRisk = slaRisk?.length || 0;
      const resolvedTodayCount = resolvedToday?.length || 0;
      const myGroupTicketsCount = myGroupTickets?.length || 0;

      return {
        totalOpen,
        slaAtRisk,
        resolvedTodayCount,
        myGroupTicketsCount,
      };
    },
    enabled: !clientLoading,
  });

  const { data: slaMetrics, isLoading: slaLoading } = useQuery({
    queryKey: ['dashboard-sla-metrics', clientId],
    queryFn: async () => {
      // Buscar métricas de SLA por tipo
      const tipos = [
        { key: 'incidente', label: 'Incidentes' },
        { key: 'solicitacao', synonyms: ['solicitacao', 'requisicao'], label: 'Solicitações' },
        { key: 'mudanca', label: 'Mudanças' },
        { key: 'problema', label: 'Problemas' },
      ];
      const metrics = [];

      for (const tipoConfig of tipos) {
        // Corrigir chave de busca para Solicitações (aceitar solicitacao e requisicao)
        let tipoKeys = tipoConfig.synonyms || [tipoConfig.key];

        let query = supabase
          .from('solicitacoes')
          .select('id, data_limite_resolucao, data_resolucao, status, tipo')
          .in('tipo', tipoKeys)
          .not('data_limite_resolucao', 'is', null);

        if (clientId) {
          query = query.eq('client_id', clientId);
        }

        const { data, error } = await query;

        if (error) {
          console.error(`Erro ao buscar tickets de tipo(s) '${tipoKeys.join(', ')}':`, error);
          metrics.push({
            category: tipoConfig.label,
            target: 95,
            current: 0,
            total: 0,
          });
          continue;
        }

        const totalTickets = data?.length || 0;
        if (totalTickets === 0) {
          metrics.push({
            category: tipoConfig.label,
            target: 95,
            current: 0,
            total: 0,
          });
          continue;
        }

        const onTime = data?.filter(ticket => {
          if (ticket.status === 'resolvida' && ticket.data_resolucao && ticket.data_limite_resolucao) {
            return new Date(ticket.data_resolucao) <= new Date(ticket.data_limite_resolucao);
          }
          if (ticket.status !== 'resolvida' && ticket.data_limite_resolucao) {
            return new Date() <= new Date(ticket.data_limite_resolucao);
          }
          return false;
        }).length || 0;

        const currentPercentage = totalTickets > 0 ? Math.round((onTime / totalTickets) * 100) : 0;

        metrics.push({
          category: tipoConfig.label,
          target: 95,
          current: currentPercentage,
          total: totalTickets,
        });
      }

      return metrics;
    },
    enabled: !clientLoading,
  });

  return {
    tickets,
    stats,
    slaMetrics,
    isLoading: ticketsLoading || statsLoading || slaLoading || clientLoading,
  };
};
